/**
 * Question Settings Storage
 *
 * Stores user preferences for question types.
 * Currently uses localStorage, designed to support account sync in future.
 */

export interface QuestionTypeConfig {
  enabled: boolean;
  subtypes: Record<string, boolean>;
}

export interface CircuitSettings {
  ohmslaw: QuestionTypeConfig;
  resistors: QuestionTypeConfig;
  current: QuestionTypeConfig;
  power: QuestionTypeConfig;
  voltagedivider: QuestionTypeConfig;
  thevenin: QuestionTypeConfig;
  capacitor: QuestionTypeConfig;
  inductor: QuestionTypeConfig;
}

export interface QuestionSettings {
  circuits: CircuitSettings;
}

// Default settings - all types and subtypes enabled
export const defaultSettings: QuestionSettings = {
  circuits: {
    ohmslaw: {
      enabled: true,
      subtypes: {
        solveForCurrent: true,
        solveForVoltage: true,
        solveForResistance: true,
      },
    },
    resistors: {
      enabled: true,
      subtypes: {
        series: true,
        parallel: true,
      },
    },
    current: {
      enabled: true,
      subtypes: {
        node: true,
        branch: true,
        divider: true,
      },
    },
    power: {
      enabled: true,
      subtypes: {
        solveForPower: true,
        solveForCurrent: true,
        solveForVoltage: true,
        solveForResistance: true,
      },
    },
    voltagedivider: {
      enabled: true,
      subtypes: {
        findVout: true,
        findR1: true,
        findR2: true,
        findVin: true,
      },
    },
    thevenin: {
      enabled: true,
      subtypes: {
        thevenin: true,
        norton: true,
        maxPowerTransfer: true,
      },
    },
    capacitor: {
      enabled: true,
      subtypes: {
        timeConstant: true,
        chargingVoltage: true,
        dischargingVoltage: true,
        storedEnergy: true,
        seriesCapacitors: true,
        parallelCapacitors: true,
      },
    },
    inductor: {
      enabled: true,
      subtypes: {
        timeConstant: true,
        currentGrowth: true,
        currentDecay: true,
        storedEnergy: true,
        seriesInductors: true,
        parallelInductors: true,
      },
    },
  },
};

const STORAGE_KEY = "quizzler_question_settings";

/**
 * Get current settings from storage
 * Falls back to defaults if not found or invalid
 */
export function getSettings(): QuestionSettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return defaultSettings;

    const parsed = JSON.parse(stored) as QuestionSettings;
    // Merge with defaults to handle new fields added in updates
    return mergeWithDefaults(parsed);
  } catch {
    return defaultSettings;
  }
}

/**
 * Save settings to storage
 */
export function saveSettings(settings: QuestionSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    // Future: sync to account if logged in
    // syncToAccount(settings);
  } catch (error) {
    console.error("Failed to save settings:", error);
  }
}

/**
 * Get enabled question types for a category
 */
export function getEnabledTypes(category: "circuits"): string[] {
  const settings = getSettings();
  const categorySettings = settings[category];

  const enabled: string[] = [];
  for (const [type, config] of Object.entries(categorySettings)) {
    if (config.enabled) {
      enabled.push(type);
    }
  }

  return enabled.length > 0 ? enabled : Object.keys(categorySettings);
}

/**
 * Get enabled subtypes for a specific question type
 */
export function getEnabledSubtypes(category: "circuits", type: string): string[] {
  const settings = getSettings();
  const typeConfig = settings[category][type as keyof CircuitSettings];

  if (!typeConfig) return [];

  const enabled: string[] = [];
  for (const [subtype, isEnabled] of Object.entries(typeConfig.subtypes)) {
    if (isEnabled) {
      enabled.push(subtype);
    }
  }

  // If none enabled, return all
  return enabled.length > 0 ? enabled : Object.keys(typeConfig.subtypes);
}

/**
 * Pick a random enabled type
 */
export function getRandomEnabledType(category: "circuits"): string {
  const enabled = getEnabledTypes(category);
  return enabled[Math.floor(Math.random() * enabled.length)];
}

/**
 * Pick a random enabled subtype for a type
 */
export function getRandomEnabledSubtype(category: "circuits", type: string): string {
  const enabled = getEnabledSubtypes(category, type);
  return enabled[Math.floor(Math.random() * enabled.length)];
}

/**
 * Merge stored settings with defaults to handle schema changes
 */
function mergeWithDefaults(stored: Partial<QuestionSettings>): QuestionSettings {
  return {
    circuits: {
      ohmslaw: {
        ...defaultSettings.circuits.ohmslaw,
        ...stored.circuits?.ohmslaw,
        subtypes: {
          ...defaultSettings.circuits.ohmslaw.subtypes,
          ...stored.circuits?.ohmslaw?.subtypes,
        },
      },
      resistors: {
        ...defaultSettings.circuits.resistors,
        ...stored.circuits?.resistors,
        subtypes: {
          ...defaultSettings.circuits.resistors.subtypes,
          ...stored.circuits?.resistors?.subtypes,
        },
      },
      current: {
        ...defaultSettings.circuits.current,
        ...stored.circuits?.current,
        subtypes: {
          ...defaultSettings.circuits.current.subtypes,
          ...stored.circuits?.current?.subtypes,
        },
      },
      power: {
        ...defaultSettings.circuits.power,
        ...stored.circuits?.power,
        subtypes: {
          ...defaultSettings.circuits.power.subtypes,
          ...stored.circuits?.power?.subtypes,
        },
      },
      voltagedivider: {
        ...defaultSettings.circuits.voltagedivider,
        ...stored.circuits?.voltagedivider,
        subtypes: {
          ...defaultSettings.circuits.voltagedivider.subtypes,
          ...stored.circuits?.voltagedivider?.subtypes,
        },
      },
      thevenin: {
        ...defaultSettings.circuits.thevenin,
        ...stored.circuits?.thevenin,
        subtypes: {
          ...defaultSettings.circuits.thevenin.subtypes,
          ...stored.circuits?.thevenin?.subtypes,
        },
      },
      capacitor: {
        ...defaultSettings.circuits.capacitor,
        ...stored.circuits?.capacitor,
        subtypes: {
          ...defaultSettings.circuits.capacitor.subtypes,
          ...stored.circuits?.capacitor?.subtypes,
        },
      },
      inductor: {
        ...defaultSettings.circuits.inductor,
        ...stored.circuits?.inductor,
        subtypes: {
          ...defaultSettings.circuits.inductor.subtypes,
          ...stored.circuits?.inductor?.subtypes,
        },
      },
    },
  };
}

// Type labels for display
export const typeLabels: Record<string, string> = {
  ohmslaw: "Ohm's Law",
  resistors: "Resistors",
  current: "Current",
  power: "Power",
  voltagedivider: "Voltage Divider",
  thevenin: "Network Theorems",
  capacitor: "Capacitors",
  inductor: "Inductors",
};

export const subtypeLabels: Record<string, Record<string, string>> = {
  ohmslaw: {
    solveForCurrent: "Solve for Current (I)",
    solveForVoltage: "Solve for Voltage (V)",
    solveForResistance: "Solve for Resistance (R)",
  },
  resistors: {
    series: "Series Combinations",
    parallel: "Parallel Combinations",
  },
  current: {
    node: "Node Analysis (KCL)",
    branch: "Branch Current Division",
    divider: "Current Divider Rule",
  },
  power: {
    solveForPower: "Solve for Power (P = IV)",
    solveForCurrent: "Solve for Current (P/V)",
    solveForVoltage: "Solve for Voltage (P/I)",
    solveForResistance: "Solve for Resistance (V²/P)",
  },
  voltagedivider: {
    findVout: "Find Output Voltage",
    findR1: "Find R1 (Top Resistor)",
    findR2: "Find R2 (Bottom Resistor)",
    findVin: "Find Input Voltage",
  },
  thevenin: {
    thevenin: "Thevenin Equivalent",
    norton: "Norton Equivalent",
    maxPowerTransfer: "Maximum Power Transfer",
  },
  capacitor: {
    timeConstant: "RC Time Constant",
    chargingVoltage: "Charging Voltage",
    dischargingVoltage: "Discharging Voltage",
    storedEnergy: "Stored Energy",
    seriesCapacitors: "Series Capacitors",
    parallelCapacitors: "Parallel Capacitors",
  },
  inductor: {
    timeConstant: "RL Time Constant",
    currentGrowth: "Current Growth",
    currentDecay: "Current Decay",
    storedEnergy: "Stored Energy",
    seriesInductors: "Series Inductors",
    parallelInductors: "Parallel Inductors",
  },
};
